<template>
  <v-container class="pa-0" fluid>
    <v-card
        class="pa-2"
        width="100%"
        :class="{ 'no-drop': isAdmin || isSeaOperator }"
    >
      <div v-if="isAdmin || isSeaOperator" class="d-flex justify-end mb-2">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
                v-on="on"
                small
                outlined
                color="primary"
                @click="openMultiUploadDialog"
            >
              {{ $t('label.uploadDocument') }}
              <v-icon>mdi-upload</v-icon>
            </v-btn>
          </template>
          <span class="text-caption">{{ $t('label.uploadDocument') }}</span>
        </v-tooltip>
      </div>

        <v-expansion-panels
            v-model="openPanels"
            multiple
            flat
            class="folders-panels"
        >
          <v-expansion-panel
              v-for="folder in visibleFolders"
              :key="folder.party"
              :class="{ 'panel-drag-over': draggingParty === folder.party }"
          >

            <v-expansion-panel-header
                @dragover.prevent="!isAdmin && !isSeaOperator && (draggingParty = folder.party)"
                @dragenter.prevent="!isAdmin && !isSeaOperator && (draggingParty = folder.party)"
                @dragleave.prevent="!isAdmin && !isSeaOperator && (draggingParty = null)"
                @drop.prevent="!isAdmin && !isSeaOperator && onDrop($event, folder.party)"
            >
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">
                    {{ folder.files.length ? 'mdi-folder' : 'mdi-folder-outline' }}
                  </v-icon>
                  <span class="font-weight-medium">
                  {{ $t(`label.party.${folder.party}`) }}
                </span>
                  <v-chip
                      class="ml-2"
                      x-small
                      color="grey lighten-3"
                  >
                    {{ folder.files.length }}
                  </v-chip>
                </div>

                <div class="d-flex align-center">
                  <v-tooltip v-if="!isAdmin && !isSeaOperator" bottom>
                    <template #activator="{ on }">
                      <v-btn
                          v-on="on"
                          icon
                          small
                          color="primary"
                          class="mr-2"
                          @click.stop="selectFile(folder.party)"
                      >
                        <v-icon>mdi-upload</v-icon>
                      </v-btn>
                    </template>
                    <span class="text-caption">
                    {{ $t('label.uploadDocument') }}
                  </span>
                  </v-tooltip>

                  <ButtonConfirmDialog
                      v-if="selected[folder.party]?.length"
                      :icon="'mdi-delete'"
                      :color="'error'"
                      :body="$t('message.confirmDeletion')"
                      :title="$t('label.confirmDelete')"
                      @submit="deleteSelected(folder.party)"
                  />
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-data-table
                  v-model="selected[folder.party]"
                  :headers="headers"
                  :items="folder.files"
                  :loading="folder.loading"
                  item-key="id"
                  :items-per-page="1000"
                  hide-default-footer
                  show-select
                  fixed-header
                  dense
                  height="100%"
                  class="scrollable"
                  no-data-text="No data available"
              >
                <template v-slot:item.fileName="{ item }">
                  <v-icon color="primary" small class="mr-2">
                    {{ getIcon(item) }}
                  </v-icon>
                  <span class="text-body-2">{{ item.fileName }}</span>
                </template>
                <template v-slot:item.createDatetime="{ item }">
                  <span class="text-body-2">{{ formatDate(item.createDatetime) }}</span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <div class="d-flex justify-end align-center">
                    <v-tooltip v-if="isAdmin">
                      <template #activator="{ on }">
                        <v-btn
                            v-on="on"
                            icon
                            small
                            color="primary"
                            @click="openCopyDialog(item)"
                        >
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </template>
                      <span class="text-caption">{{ $t('label.copyToFolder') }}</span>
                    </v-tooltip>
                    <PraticaDocumentActions
                        :document="item"
                        :canDelete="canDelete"
                        @delete-file="(id) => deleteFile(id, folder.party)"
                        @download-file="downloadDocument"
                    />
                  </div>
                </template>
              </v-data-table>

            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
    </v-card>

    <input
        ref="fileUpload"
        type="file"
        hidden
        @change="($event) => uploadFile($event?.target?.files?.[0])"
    />

    <v-dialog v-model="multiUploadDialog" max-width="400">
      <v-card>
        <v-card-title class="primary white--text text-caption py-2 px-3">
          {{ $t('label.uploadDocument') }}
        </v-card-title>
        <v-card-text class="pa-4">
          <p class="mb-3 text-body-2">{{ $t('message.selectFoldersForUpload') }}</p>
          <v-checkbox
              v-for="party in PARTIES"
              :key="party"
              v-model="multiUploadParties"
              :label="$t(`label.party.${party}`)"
              :value="party"
              dense
              hide-details
          >
            <template #label>
              <span class="text-body-2">{{ $t(`label.party.${party}`) }}</span>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions class="justify-start px-2">
          <v-btn
              class="primary"
              small
              :disabled="!multiUploadParties.length"
              @click="confirmMultiUpload"
          >
            {{ $t('label.confirm') }}
          </v-btn>
          <v-btn class="error" small @click="multiUploadDialog = false">
            {{ $t('label.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="copyDialog" max-width="400">
      <v-card>
        <v-card-title class="primary white--text text-caption py-2 px-3">
          {{ $t('label.copyToFolder') }}
        </v-card-title>
        <v-card-text class="pa-4">
          <p class="mb-3 text-body-2">{{ $t('message.selectFoldersToCopy') }}</p>
          <v-checkbox
              v-for="party in availableCopyTargets"
              :key="party"
              v-model="copyTargetParties"
              :value="party"
              dense
              hide-details
              class="mt-1"
          >
            <template #label>
              <span class="text-body-2">{{ $t(`label.party.${party}`) }}</span>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions class="justify-start px-2">
          <v-btn
              class="primary"
              small
              :disabled="!copyTargetParties.length"
              @click="confirmCopy"
          >
            {{ $t('label.confirm') }}
          </v-btn>
          <v-btn class="error" small @click="copyDialog = false">
            {{ $t('label.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import {mapMutations, mapState} from 'vuex'
import FileSaver from 'file-saver'
import {dateFilter} from '@/filters/dateFilter'
import {documentsService} from '@/services/documents.service.js'
import PraticaDocumentActions from '@/components/elements/pratica/PraticaDocumentActions'
import ButtonConfirmDialog from '@/components/elements/global/ButtonConfirmDialog'

const PARTIES = ['SHIPPER', 'CONSIGNEE', 'CLIENT_FM', 'CLIENT_TO']

export default {
  name: 'HouseBLDocuments',
  props: ['pratica'],
  components: {ButtonConfirmDialog, PraticaDocumentActions},
  data() {
    return {
      selected: {},
      openPanels: [],
      folders: {},
      activeUploadParty: null,
      headers: [
        {text: this.$t('label.name'), value: 'fileName'},
        {text: this.$t('label.createDate'), value: 'createDatetime'},
        {value: 'actions', sortable: false},
      ],
      multiUploadDialog: false,
      multiUploadParties: [],
      copyDialog: false,
      copyTargetParties: [],
      copySourceDocument: null,
      PARTIES: PARTIES,
      draggingParty: null,
    }
  },
  computed: {
    ...mapState('account', ['user', 'isAdmin', 'isSeaOperator']),

    visibleParties() {
      if (this.isAdmin || this.isSeaOperator) {
        return PARTIES
      }

      const checks = [
        {
          party: 'SHIPPER',
          list: this.user?.shippers,
          target: this.pratica?.shipperId,
        },
        {
          party: 'CONSIGNEE',
          list: this.user?.consignees,
          target: this.pratica?.consigneeId,
        },
        {
          party: 'CLIENT_FM',
          list: this.user?.customers,
          target: this.pratica?.clientFmId,
        },
        {
          party: 'CLIENT_TO',
          list: this.user?.clientsTo,
          target: this.pratica?.clientToId,
        },
      ]

      return checks
          .filter(({list, target}) =>
              list?.some(item => item.id === target)
          )
          .map(({party}) => party)
    },
    visibleFolders() {
      return this.visibleParties.map((party) => this.folders[party] || {party, files: [], loading: false})
    },
    availableCopyTargets() {
      if (!this.copySourceDocument) return PARTIES
      const currentParties = this.copySourceDocument.systemTags.PARTY
          .split('.')
          .map((p) => p.trim())
      return PARTIES.filter((p) => !currentParties.includes(p))
    },
  },
  methods: {
    browseDocuments() {
      this.initFolders()

      this.visibleParties.forEach((party) => {
        this.browseDocumentsForParty(party)
      })
    },
    initFolders() {
      const folders = {}
      const selected = {}
      this.visibleParties.forEach((party) => {
        folders[party] = {party, files: [], loading: false}
        selected[party] = []
      })
      this.folders = folders
      this.selected = selected
      // Collapsed by default
      this.openPanels = []
    },
    browseDocumentsForParty(party) {
      this.folders[party].loading = true

      const filter = {
        systemTagsAND: JSON.stringify({
          tagFilters: [
            {key: 'DOCUMENT_NBR', value: this.pratica.documentNbr},
            {key: 'TENANT_ID', value: this.pratica.tenantId},
          ],
        }),
        systemTagsContains: JSON.stringify({
          tagFilters: [{key: 'PARTY', value: party}],
        }),
      }

      const browseRequest = {
        offset: 0,
        limit: 1000,
        filter,
        sortingList: [],
      }

      documentsService
          .browse(browseRequest)
          .then((resp) => {
            if (!resp) return
            this.folders[party].files = resp.resultList.filter((d) => d.deleteDatetime === null)
          })
          .catch(() => this.setSnackbarMessage({text: `${this.$t('message.errorRetrievingDocs')}`, color: 'error'}))
          .finally(() => {
            this.folders[party].loading = false
          })
    },
    selectFile(party) {
      this.activeUploadParty = party
      this.selected[party] = []
      this.$refs.fileUpload.click()
    },
    openMultiUploadDialog() {
      this.multiUploadParties = []
      this.multiUploadDialog = true
    },
    confirmMultiUpload() {
      if (!this.multiUploadParties.length) return
      this.multiUploadDialog = false
      this.$refs.fileUpload.click()
    },
    uploadFile(file) {
      if (!file) return

      // Reset input so the same file can be selected again consecutively
      this.$refs.fileUpload.value = ''

      if (this.isAdmin || this.isSeaOperator) {
        this.setLoading(true)
        this.getBase64(file)
            .then(async (data) => {
              const dataFile = {
                fileName: file.name,
                documentType: 'UNDEFINED',
                systemTags: this.getTags(file, this.multiUploadParties.join('.')),
                dataB64: data,
              }
              await this.createDocument(dataFile, this.multiUploadParties)
            })
            .catch(() => this.setSnackbarMessage({text: `${this.$t('message.errorConvertingFile')}`, color: 'error'}))
            .finally(() => this.setLoading(false))
        return
      }
      this.setLoading(true)
      this.getBase64(file)
          .then((data) => {
            const dataFile = {
              fileName: file.name,
              documentType: 'UNDEFINED',
              systemTags: this.getTags(file, this.activeUploadParty),
              dataB64: data,
            }
            this.createDocument(dataFile, this.activeUploadParty)
          })
          .catch(() => this.setSnackbarMessage({text: `${this.$t('message.errorConvertingFile')}`, color: 'error'}))
    },
    createDocument(data, party) {
      documentsService
          .createDocument(data)
          .then(() => {
            this.setSnackbarMessage({text: this.$t('message.docSuccessfullyUploaded'), color: 'success'})
            const parties = Array.isArray(party) ? party : [party]
            parties.forEach((p) => this.browseDocumentsForParty(p))
          })
          .catch(() => this.setSnackbarMessage({text: `${this.$t('message.errorUploadingDoc')}`, color: 'error'}))
          .finally(() => this.setLoading(false))
    },
    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          const base64String = reader.result.replace('data:', '').replace(/^.+,/, '')
          resolve(base64String)
        }
        reader.onerror = (error) => reject(error)
      })
    },
    getTags(file, party) {
      return {
        DOCUMENT_NBR: this.pratica.documentNbr,
        TYPE: file.type,
        TENANT_ID: this.pratica.tenantId,
        PARTY: party,
        USER: this.user.username
      }
    },
    onDrop(e, party) {
      this.draggingParty = null
      Object.keys(e.dataTransfer.files).forEach((key) => {
        const file = e.dataTransfer.files[key]
        this.getBase64(file)
            .then((data) => {
              const dataFile = {
                fileName: file.name,
                documentType: 'UNDEFINED',
                systemTags: this.getTags(file, party),
                dataB64: data,
              }
              this.createDocument(dataFile, party)
            })
            .catch(() => this.setSnackbarMessage({text: `${this.$t('message.errorConvertingFile')}`, color: 'error'}))
      })
    },
    downloadDocument(id) {
      this.setLoading(true)
      documentsService
          .getDocument(id)
          .then((resp) => {
            if (!resp) return
            this.downloadFile(resp)

            if (this.isAdmin || this.isSeaOperator) {
              documentsService.updateDocument(id, {
                fileName: resp.fileName,
                systemTags: { DOWNLOADED_BY: 'INTERNAL' }
              })
            }

            this.setSnackbarMessage({text: this.$t('message.docSuccessfullyDownloaded'), color: 'success'})
          })
          .catch((e) => this.setSnackbarMessage({
            text: `${this.$t('message.errorDownloadingDoc')}: ${e.error}`,
            color: 'error'
          }))
          .finally(() => this.setLoading(false))
    },
    downloadFile(value) {
      const blob = new Blob([Buffer.from(value.dataB64, 'base64')])
      FileSaver.saveAs(blob, value.fileName)
    },
    async deleteFile(id, party) {
      this.setLoading(true)
      const existingFile = await documentsService.getDocument(id)

      if (!this.canDelete(existingFile)) {
        this.setSnackbarMessage({
          text: this.$t('message.errorDocAlreadyDownloaded'),
          color: 'error'
        })
        this.setLoading(false)
        return
      }

      const currentParties = existingFile.systemTags.PARTY.split('.').map((p) => p.trim())
      const updatedParties = currentParties.filter((p) => p !== party)

      if (updatedParties.length === 0) {
        // last folder — actually delete the document
        this.deleteDocument(id).then(() => {
          this.setSnackbarMessage({text: this.$t('message.docSuccessfullyDeleted'), color: 'success'})
          this.browseDocumentsForParty(party)
        })
      } else {
        documentsService.updateDocument(id, {
          fileName: existingFile.fileName,
          systemTags: {PARTY: updatedParties.join('.')}
        })
            .then(() => {
              this.setSnackbarMessage({text: this.$t('message.docSuccessfullyDeleted'), color: 'success'})
              this.browseDocumentsForParty(party)
            })
            .catch(() => this.setSnackbarMessage({
              text: `${this.$t('message.errorDeletingDoc')}`,
              color: 'error'
            }))
            .finally(() => this.setLoading(false))
      }
    },
    async deleteSelected(party) {
      this.setLoading(true)
      const docs = this.selected[party] || []
      const skipped = []

      for (const doc of docs) {
        const existingFile = await documentsService.getDocument(doc.id)

        if (!this.canDelete(existingFile)) {
          skipped.push(doc.fileName)
          continue
        }

        const currentParties = existingFile.systemTags.PARTY.split('.').map((p) => p.trim())
        const updatedParties = currentParties.filter((p) => p !== party)

        if (updatedParties.length === 0) {
          await this.deleteDocument(doc.id)
        } else {
          await documentsService
              .updateDocument(doc.id, {
                fileName: existingFile.fileName,
                systemTags: {PARTY: updatedParties.join('.')}
              })
              .catch(() => this.setSnackbarMessage({
                text: `${this.$t('message.errorDeletingDoc')}`,
                color: 'error'
              }))
        }
      }

      if (skipped.length) {
        this.setSnackbarMessage({
          text: `${this.$t('message.someDocsSkipped')}: ${skipped.join(', ')}`,
          color: 'warning'
        })
      } else {
        this.setSnackbarMessage({ text: this.$t('message.docsSuccessfullyDeleted'), color: 'success' })
      }

      this.browseDocumentsForParty(party)
      this.selected[party] = []
      this.setLoading(false)
    },
    deleteDocument(id) {
      return documentsService
          .deleteDocument(id)
          .catch(() => this.setSnackbarMessage({text: `${this.$t('message.errorDeletingDoc')}`, color: 'error'}))
          .finally(() => this.setLoading(false))
    },
    canDelete(item) {
      if (this.isAdmin || this.isSeaOperator) return true
      return item.systemTags.USER === this.user.username && !item.systemTags.DOWNLOADED_BY
    },
    formatDate(date) {
      return dateFilter.formatDate(date)
    },
    getIcon(doc) {
      const type = doc.systemTags.TYPE
      switch (type) {
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return 'mdi-file-word-box'
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return 'mdi-file-excel-box'
        case 'application/pdf':
          return 'mdi-file-pdf-box'
        default:
          return 'mdi-text-box'
      }
    },
    openCopyDialog(item) {
      this.copySourceDocument = item
      this.copyTargetParties = []
      this.copyDialog = true
    },
    async confirmCopy() {
      if (!this.copyTargetParties.length || !this.copySourceDocument) return
      this.copyDialog = false
      this.setLoading(true)

      const currentParties = this.copySourceDocument.systemTags.PARTY
          .split('.')
          .map((p) => p.trim())

      // merge current parties with selected targets, avoid duplicates
      const mergedParties = [...new Set([...currentParties, ...this.copyTargetParties])]

      try {
        await documentsService.updateDocument(
            this.copySourceDocument.id,
            {
              fileName: this.copySourceDocument.fileName,
              systemTags: {PARTY: mergedParties.join('.')}
            }
        )
        this.setSnackbarMessage({text: this.$t('message.docSuccessfullyCopied'), color: 'success'})
        // refresh all affected folders
        mergedParties.forEach((p) => this.browseDocumentsForParty(p))
      } catch {
        this.setSnackbarMessage({text: `${this.$t('message.errorCopyingDoc')}`, color: 'error'})
      } finally {
        this.copySourceDocument = null
        this.copyTargetParties = []
        this.setLoading(false)
      }
    },
  },
  created() {
    this.browseDocuments()
  },
}
</script>

<style scoped>
.folders-panels {
  width: 100%;
}

.folders-panels .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px !important;
  margin-bottom: 8px;
}

.folders-panels .v-expansion-panel::before {
  box-shadow: none;
}

.folders-panels .v-expansion-panel-header {
  min-height: 48px;
}

.folders-panels .v-expansion-panel-content__wrap {
  padding-bottom: 16px;
}

.folders-panels .v-data-table >>> td,
.folders-panels .v-data-table >>> th {
  padding: 0 12px;
  height: 36px;
}

.scrollable {
  height: 100%;
  overflow-y: auto;
}

.scrollable::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  width: 5px;
}

.scrollable::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: var(--v-primary-lighten1);
  border-radius: 20px;
}

.no-drop {
  cursor: default;
}

.panel-drag-over {
  border: dashed 2px var(--v-primary-base) !important;
  background-color: rgba(46, 84, 150, 0.04);
}
</style>